/* Common Imports */

import React from "react";
import { styled } from "@mui/system";

/* MUI Imports */

import { Autocomplete, Chip, TextField, Typography } from "@mui/material";
import { Close } from "@mui/icons-material";

/* Google Maps Imports */

import { useLoadScript } from "@react-google-maps/api";
import usePlacesAutocomplete, { getGeocode, getLatLng } from "use-places-autocomplete";

/* Styled Components */

const OptionContainer = styled("li")(() => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "flex-start",
	alignItems: "flex-start",
	width: "100%",
	padding: "0.5rem 0.75rem",
}));

const OptionMainText = styled(Typography)(() => ({
	fontSize: "0.875rem",
	fontWeight: 500,
	color: "#212121",
}));

const OptionSubText = styled(Typography)(() => ({
	fontSize: "0.75rem",
	fontWeight: 400,
	color: "#9e9e9e",
}));

/* Libraries to load */

const librariesToLoad: ("places" | "drawing" | "geometry")[] = ["places"];

/* Location data interface */

interface LocationData {
	location_title: string;
	state: string;
	city: string;
	micro_market: string;
	locality: string;
	location_coordinates: Array<number>;
}

/* Component */

const LocationAutocompleteMulti = ({
	selectedLocations,
	onLocationsChange,
}: {
	selectedLocations: Array<LocationData>;
	onLocationsChange: (locations: Array<LocationData>) => void;
}) => {
	/* Google Maps script loading */

	const { isLoaded } = useLoadScript({
		googleMapsApiKey: process.env.GOOGLE_MAPS_API_KEY_NO_REFERRER_RESTRICTIONS || "",
		libraries: librariesToLoad as any,
	});

	/* Places autocomplete hook */

	const {
		value,
		suggestions: { data },
		setValue,
		init,
	} = usePlacesAutocomplete({
		initOnMount: false,
		debounce: 500,
		requestOptions: {
			componentRestrictions: { country: "in" },
		},
	});

	/* Initialize autocomplete when script is loaded */

	React.useEffect(() => {
		if (isLoaded) {
			init();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLoaded]);

	/* Build options from suggestions */

	const options = React.useMemo(() => {
		return data.map((suggestion) => ({
			place_id: suggestion.place_id,
			main_text: suggestion.structured_formatting.main_text,
			secondary_text: suggestion.structured_formatting.secondary_text,
			description: suggestion.description,
		}));
	}, [data]);

	/* Handle selection */

	const handleSelect = async (_event: any, selectedOption: any) => {
		if (!selectedOption) return;

		/* Check if already added */

		if (selectedLocations.length >= 3) return;

		const alreadyAdded = selectedLocations.some(
			(loc) => loc.location_title === selectedOption.main_text,
		);
		if (alreadyAdded) {
			setValue("");
			return;
		}

		try {
			/* Get coordinates from place ID */

			const results = await getGeocode({ placeId: selectedOption.place_id });
			const { lat, lng } = getLatLng(results[0]);

			/* Extract address components */

			const addressComponents = results[0].address_components;
			let city = "";
			let state = "";
			let locality = "";
			let microMarket = "";

			for (const component of addressComponents) {
				if (component.types.includes("sublocality_level_1")) {
					locality = component.long_name;
				}
				if (component.types.includes("sublocality_level_2")) {
					microMarket = component.long_name;
				}
				if (component.types.includes("locality") || component.types.includes("postal_town")) {
					city = component.long_name;
				}
				if (component.types.includes("administrative_area_level_1")) {
					state = component.long_name;
				}
			}

			const newLocation: LocationData = {
				location_title: selectedOption.main_text,
				state: state,
				city: city,
				micro_market: microMarket,
				locality: locality || selectedOption.main_text,
				location_coordinates: [lng, lat],
			};

			onLocationsChange([...selectedLocations, newLocation]);
			setValue("");
		} catch (error) {
			console.error("Error geocoding location:", error);
		}
	};

	/* Handle remove */

	const handleRemove = (index: number) => {
		const updated = selectedLocations.filter((_, i) => i !== index);
		onLocationsChange(updated);
	};

	return (
		<div>
			{/* Selected location chips */}

			{selectedLocations.length > 0 && (
				<div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", marginBottom: "0.5rem" }}>
					{selectedLocations.map((loc, index) => (
						<Chip
							key={index}
							label={loc.location_title}
							onDelete={() => handleRemove(index)}
							deleteIcon={<Close sx={{ fontSize: "0.875rem" }} />}
							variant="outlined"
							sx={{
								backgroundColor: "#FFF8F3",
								borderColor: "#E07934",
								color: "#E07934",
								"& .MuiChip-deleteIcon": { color: "#E07934" },
							}}
						/>
					))}
				</div>
			)}

			{/* Autocomplete input */}

			<Autocomplete
				freeSolo
				disabled={selectedLocations.length >= 3}
				options={options}
				getOptionLabel={(option: any) => (typeof option === "string" ? option : option.main_text)}
				inputValue={value}
				onInputChange={(_event, newValue) => {
					setValue(newValue);
				}}
				onChange={handleSelect}
				filterOptions={(x) => x}
				renderOption={(props, option: any) => {
					const { key, ...rest } = props as any;
					return (
						<OptionContainer {...rest} key={option.place_id} style={{ ...rest.style, textAlign: "left" }}>
							<OptionMainText>{option.main_text}</OptionMainText>
							<OptionSubText>{option.secondary_text}</OptionSubText>
						</OptionContainer>
					);
				}}
				renderInput={(params) => (
					<TextField
						{...params}
						size="small"
						placeholder={selectedLocations.length >= 3 ? "Maximum 3 locations reached" : selectedLocations.length === 0 ? "Search for a location" : "Add another location"}
							helperText={`${selectedLocations.length}/3 locations added`}
					/>
				)}
				slotProps={{
					paper: {
						sx: { "& .MuiAutocomplete-option": { justifyContent: "flex-start", textAlign: "left" } },
					},
				}}
				noOptionsText="Type to search locations"
				value={null}
				blurOnSelect
				clearOnBlur
			/>
		</div>
	);
};

export default LocationAutocompleteMulti;
